import * as Sentry from "@sentry/react";
import { createBrowserRouter, Navigate, Outlet, RouteObject, useLocation } from "react-router";
import { RouterProvider } from "react-router/dom";

import { Root } from "./Root";
import { EmbeddingLayout } from "./Layout/EmbeddingLayout";
import { ThoughtSpotLayout } from "./Layout/ThoughtSpotLayout";
import { SecondaryLayout } from "./Layout/SecondaryLayout";
import { ErrorBoundary } from "./ErrorBoundary";
import { Protected } from "./Protected";
import { Unprotected } from "./Unprotected";
import { ThoughtspotInitializer, ThoughtspotProtected } from "./ThoughtspotProtected";
import { LogoutDialog } from "./LogoutDialog";
import { BusinessContextGuard } from "./guards/BusinessContextGuard";
import { OnboardingProgressProvider } from "./onboardingStatus";

import { SearchEmbedPage } from "./pages/SearchEmbed";
import { Spotter } from "./pages/SpotterEmbed";
import { AppEmbedPage } from "./pages/AppEmbed";
import { LiveboardEmbed } from "./pages/LiveboardEmbed";
import { SEmbedHome } from "./pages/SEmbed";
import { AIDashboards } from "./pages/AIDashboards";
import { AISchedules } from "./pages/AISchedules";
import {
  AI_DASHBOARDS_PATH,
  AI_SCHEDULES_PATH,
  CHAT_PATH,
  DASHBOARDS_PATH,
  DASHBOARDS_ROUTE_ID,
} from "./constants";

import { Login } from "./pages/Login";
import { ResetPassword } from "./pages/ResetPassword";
import { Settings } from "./pages/Settings";
import { SettingsUsers } from "./pages/Settings/Users";
import { SettingsBusinesses } from "./pages/Settings/Businesses";
import { SettingsEmailSchedules } from "./pages/Settings/EmailSchedules";
import { SettingsBudget } from "./pages/Settings/Budget";

// Onboarding Components

function ThoughtSpotProtectedLayout() {
  const location = useLocation();
  const feature = location.pathname.startsWith("/schedules") ? "Schedules" : "Dashboards";

  return (
    <ThoughtspotProtected feature={feature}>
      <ThoughtSpotLayout />
    </ThoughtspotProtected>
  );
}

function LegacyChatRedirect() {
  const location = useLocation();
  const legacyPath = "/labeeb-chat";
  const suffix = location.pathname.slice(legacyPath.length);

  return <Navigate to={`${CHAT_PATH}${suffix}${location.search}${location.hash}`} replace />;
}

const routes: RouteObject[] = [
  {
    path: "/",
    errorElement: <ErrorBoundary />,
    HydrateFallback: ErrorBoundary,
    element: <Root />,
    children: [
      {
        path: "login",
        element: (
          <Unprotected>
            <Login />
          </Unprotected>
        ),
      },
      // Password-reset links sent to older mobile builds need a browser fallback.
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "install",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "already-installed",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "multi-business/success",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "multi-business/already-installed",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "initial-onboarding",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "onboarding/error",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "onboarding/:businessId",
        element: <Navigate to="/login" replace />,
      },
      {
        path: "/",
        element: (
          <Protected>
            <OnboardingProgressProvider>
              <BusinessContextGuard>
                <ThoughtspotInitializer>
                  <Outlet />
                </ThoughtspotInitializer>
              </BusinessContextGuard>
            </OnboardingProgressProvider>
            <LogoutDialog />
          </Protected>
        ),
        children: [
          {
            path: "settings",
            element: <Settings />,
            children: [
              {
                path: "users",
                element: <SettingsUsers />,
              },
              {
                path: "businesses",
                element: <SettingsBusinesses />,
              },
              {
                path: "email_schedules",
                element: (
                  <ThoughtspotProtected feature="Email schedules">
                    <SettingsEmailSchedules />
                  </ThoughtspotProtected>
                ),
              },
              {
                path: "budget",
                element: <SettingsBudget />,
              },
            ],
          },
          {
            element: <EmbeddingLayout />,
            children: [
              {
                index: true,
                element: <Navigate to={CHAT_PATH} replace />,
              },
              {
                path: `${CHAT_PATH.slice(1)}/*`,
                element: <SEmbedHome />,
              },
              {
                id: DASHBOARDS_ROUTE_ID,
                element: <ThoughtSpotProtectedLayout />,
                children: [
                  {
                    path: DASHBOARDS_PATH.slice(1),
                    element: (
                      <ThoughtspotProtected feature="Liveboards">
                        <LiveboardEmbed />
                      </ThoughtspotProtected>
                    ),
                  },
                  {
                    path: "schedules/*",
                    element: (
                      <ThoughtspotProtected feature="Schedules">
                        <AppEmbedPage />
                      </ThoughtspotProtected>
                    ),
                  },
                  {
                    path: "*",
                    element: (
                      <ThoughtspotProtected feature="Liveboards">
                        <LiveboardEmbed />
                      </ThoughtspotProtected>
                    ),
                  },
                ],
              },
              {
                element: <SecondaryLayout />,
                children: [
                  {
                    path: "answer/*",
                    element: (
                      <ThoughtspotProtected feature="Search">
                        <SearchEmbedPage />
                      </ThoughtspotProtected>
                    ),
                  },
                  {
                    path: "spotter/*",
                    element: (
                      <ThoughtspotProtected feature="Spotter">
                        <Spotter />
                      </ThoughtspotProtected>
                    ),
                  },
                  {
                    path: "labeeb-chat/*",
                    element: <LegacyChatRedirect />,
                  },
                  {
                    path: AI_DASHBOARDS_PATH.slice(1),
                    element: <AIDashboards />,
                  },
                  {
                    path: AI_SCHEDULES_PATH.slice(1),
                    element: <AISchedules />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

// Export routes for testing purposes
/* eslint-disable react-refresh/only-export-components */
export { routes };

const router = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter)(routes);

export function Router() {
  return <RouterProvider router={router} />;
}
